// NotificationPopover.js
import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Popover,
    List,
    ListItem,
    ListItemText,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useServices } from "../providers/service-provider";
import { useSendNotification } from "./useSendNotification";

const LOCAL_STORAGE_KEY = "sentNotifications";

const NotificationPopover = ({ open, anchorEl, onClose, selectedUserId }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { authorizedHttpClient } = useServices();
    const sendNotification = useSendNotification(authorizedHttpClient);

    const [templateKey, setTemplateKey] = useState("");
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [sentMessages, setSentMessages] = useState([]);

    // Compute the user-specific storage key
    const storageKey = `${LOCAL_STORAGE_KEY}_${selectedUserId}`;
    const TEMPLATES = [
        { key: "motivation", label: "Motivation", title: "Du machst das super!", message: "Dein Engagement zahlt sich aus – bleib dran!" },
        { key: "info", label: "Info", title: "Info des Tages", message: "Kleine Schwankungen sind normal. Wichtig ist der Trend!" },
        { key: "insight", label: "Tipp des Tages", title: "Balance ist alles", message: "Ein ausgewogenes Frühstück kann Wunder wirken." },
        { key: "low_warning", label: "Achtung niedrig", title: "Kurze Pause einlegen?", message: "Gönn dir vielleicht einen kleinen Snack 🥨" },
        { key: "high_warning", label: "Achtung hoch", title: "Körper in Balance", message: "Vielleicht ein kurzer Spaziergang nach dem Essen?" },
        { key: "coffee_time", label: "Kaffeezeit ☕️", title: "Zeit für eine Pause?", message: "Ein entspannter Kaffee kann genauso wichtig sein wie Daten." },
        { key: "hydration_reminder", label: "Trinken 💧", title: "Wasser marsch!", message: "Hydration ist unterschätzt – jetzt ein Glas Wasser?" },
        { key: "recheck", label: "Erneut messen", title: "Check nochmal", message: "Wie wär’s mit einer Kontrollmessung – just to be sure." },
        { key: "breakfast", label: "Frühstück", title: "Morgenroutine aktivieren", message: "Ein ausgewogenes Frühstück ist dein Start in den Tag." },
        { key: "evening_check", label: "Abend-Check", title: "Ruhe reinbringen", message: "Ein kurzer Blick auf deinen Tagesverlauf – alles im Lot?" },
        { key: "weekend_mode", label: "Wochenende 🛋️", title: "Mach es dir schön!", message: "Auch am Wochenende: kleine Checks nicht vergessen." },
        { key: "movement", label: "Bewegung", title: "Ein paar Schritte?", message: "Ein Spaziergang kann nicht nur dem Blutzucker guttun." },
        { key: "meal_prep", label: "Essensplanung", title: "Plan ist Power", message: "Plane dein nächstes Essen – dein Körper wird’s dir danken." },
        { key: "check_in", label: "Stimmungscheck", title: "Wie fühlst du dich?", message: "Glukose ist nicht alles – wie geht’s dir heute?" },
        { key: "relax", label: "Entspannung", title: "Kurz durchatmen", message: "Atme tief durch – auch das stabilisiert mehr als du denkst." },
        { key: "reflection", label: "Reflexion", title: "Heute gut gemeistert?", message: "Nimm dir einen Moment, um stolz auf dich zu sein ✨" },
        { key: "treat", label: "Belohnung", title: "Kleine Belohnung?", message: "Warum nicht etwas, das dir Freude macht – du hast’s verdient." },
        { key: "sleep", label: "Schlaf", title: "Gute Nacht 🌙", message: "Erholsamer Schlaf unterstützt deine Werte – ab ins Bett." },
        { key: "positivity", label: "Positiver Start", title: "Neuer Tag, neue Chance", message: "Egal wie gestern war – heute zählt." },
        { key: "nature", label: "Naturmoment", title: "Frische Luft tanken", message: "Ein bisschen draußen sein hilft Körper und Geist." }
    ];
    // Whenever selectedUserId changes, reload that user's history
    useEffect(() => {
        const raw = localStorage.getItem(storageKey) || "[]";
        setSentMessages(JSON.parse(raw));
    }, [storageKey]);

    const saveToHistory = (t, m) => {
        const entry = { title: t, message: m, time: new Date().toISOString() };
        const updated = [entry, ...sentMessages];
        setSentMessages(updated);
        localStorage.setItem(storageKey, JSON.stringify(updated));
    };

    const handleTemplateChange = (e) => {
        const key = e.target.value;
        setTemplateKey(key);
        const tmpl = TEMPLATES.find((t) => t.key === key);
        if (tmpl) {
            setTitle(tmpl.title);
            setMessage(tmpl.message);
        }
    };

    const handleSend = async () => {
        if (!title.trim() || !message.trim()) {
            alert("Bitte Titel und Nachricht ausfüllen.");
            return;
        }
        const resp = await sendNotification(selectedUserId, title, message);
        if (resp) {
            alert("Nachricht gesendet!");
            saveToHistory(title, message);
            setTemplateKey("");
            setTitle("");
            setMessage("");
            onClose();
        } else {
            alert("Fehler beim Senden.");
        }
    };

    const content = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Nachricht senden</Typography>

            <FormControl fullWidth>
                <InputLabel id="template-select-label">Vorlagen</InputLabel>
                <Select
                    labelId="template-select-label"
                    value={templateKey}
                    label="Vorlagen"
                    onChange={handleTemplateChange}
                >
                    <MenuItem value=""><em>Keine Vorlage</em></MenuItem>
                    {TEMPLATES.map((t) => (
                        <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <TextField
                label="Titel"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
            />
            <TextField
                label="Nachricht"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                multiline
                rows={3}
                fullWidth
            />

            <Button variant="contained" onClick={handleSend}>Senden</Button>

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1">Gesendete Nachrichten</Typography>
            <List dense>
                {sentMessages.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        Noch keine Nachrichten.
                    </Typography>
                )}
                {sentMessages.map((msg, i) => (
                    <ListItem key={i} alignItems="flex-start">
                        <ListItemText
                            primary={msg.title}
                            secondary={
                                <>
                                    {msg.message}
                                    <br />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        component="span"
                                    >
                                        {dayjs(msg.time).format("DD.MM.YYYY HH:mm")}
                                    </Typography>
                                </>
                            }
                        />
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    // Mobile: Popover
    if (isMobile) {
        return (
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ sx: { width: "90vw", maxHeight: 500, overflowY: "auto", p: 2 } }}
            >
                {content}
            </Popover>
        );
    }

    // Desktop: Centered Dialog
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Nachricht senden</DialogTitle>
            <DialogContent dividers sx={{ p: 2 }}>
                {content}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Abbrechen</Button>
            </DialogActions>
        </Dialog>
    );
};

export default NotificationPopover;
